import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "../../authentication/firebase";
import BackdropWrapper from "./BackdropWrapper";
import { Close, ShoppingCart, LocalOffer } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import LoginRequiredPopup from "./LoginRequiredPopup";

const ProductAdPopup = ({ isOpen, onClose }) => {
  const [popupData, setPopupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyClicked, setBuyClicked] = useState(false);
  const [showLoginRequired, setShowLoginRequired] = useState(false);

  // Fetch popup data from Firebase
  const fetchPopupData = async () => {
    try {
      setLoading(true);
      const popupsCollectionRef = collection(db, "popups");
      const popupQuery = query(
        popupsCollectionRef,
        where("type", "==", "product")
      );
      const popupSnapshot = await getDocs(popupQuery);

      if (!popupSnapshot.empty) {
        const fetchedPopup = popupSnapshot.docs[0].data();
        console.log("Fetched product popup data:", fetchedPopup);
        setPopupData(fetchedPopup);
      } else {
        console.log("No product-ad popup found");
      }
    } catch (error) {
      console.error("Error fetching product popup data:", error);
      setError("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPopupData();
    }
  }, [isOpen]);

  // Handle buy now - check authentication first
  const handleBuyNow = () => {
    if (popupData?.paymentLink) {
      // Check if user is logged in
      if (auth.currentUser) {
        // User is logged in, proceed with purchase
        setBuyClicked(true);

        // Open payment link in new tab
        setTimeout(() => {
          window.open(popupData.paymentLink, "_blank");
          onClose();
        }, 1500);
      } else {
        // User is not logged in, show login required popup
        setShowLoginRequired(true);
      }
    }
  };

  const popupContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <CircularProgress color="inherit" size={40} />
          <p>Loading exclusive offer...</p>
        </LoadingContainer>
      );
    }

    if (error || !popupData) {
      return (
        <ErrorContainer>
          <p>{error || "No special offers available at this time"}</p>
          <CloseButton onClick={onClose}>
            <Close />
          </CloseButton>
        </ErrorContainer>
      );
    }

    if (buyClicked) {
      return (
        <SuccessContainer>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <ShoppingCart
              style={{ fontSize: "3rem", color: "var(--primary-color)" }}
            />
          </motion.div>
          <h3>Redirecting to Checkout! 🛒</h3>
          <p>Taking you to secure payment...</p>
        </SuccessContainer>
      );
    }

    return (
      <PopupContent>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>

        <PopupContainer>
          {/* Left side - Image */}
          <ImageSection>
            {popupData.imageUrl && (
              <motion.img
                src={popupData.imageUrl}
                alt={popupData.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              />
            )}
          </ImageSection>

          {/* Right side - Content */}
          <ContentSection>
            <motion.h1
              className="main-heading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              {popupData.name}
            </motion.h1>

            <motion.p
              className="description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              {popupData.description}
            </motion.p>

            <motion.div
              className="price-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <div className="price-display">
                <LocalOffer className="price-icon" />
                <span className="price">${popupData.price}</span>
                <span className="price-label">Limited Time Offer</span>
              </div>
            </motion.div>

            <motion.div
              className="action-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <motion.button
                className="buy-btn"
                onClick={handleBuyNow}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ShoppingCart />
                BUY NOW
              </motion.button>

              <p className="disclaimer">
                Secure payment • 30-day money back guarantee
              </p>

              <button className="skip-btn" onClick={onClose}>
                Maybe later
              </button>
            </motion.div>
          </ContentSection>
        </PopupContainer>
      </PopupContent>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <BackdropWrapper
        open={isOpen}
        backdropHandler={onClose}
        smallSize={false}
        element={popupContent()}
      />

      {/* Login Required Popup */}
      <LoginRequiredPopup
        isOpen={showLoginRequired}
        onClose={() => setShowLoginRequired(false)}
        message="Please login or signup before making a purchase"
        onNavigate={onClose}
      />
    </>
  );
};

export default ProductAdPopup;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 20px;

  p {
    color: var(--text-gray-color);
    margin: 0;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 20px;
  text-align: center;

  p {
    color: var(--text-gray-color);
    margin: 0;
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 20px;
  text-align: center;

  h3 {
    color: var(--text-color);
    margin: 0;
    font-size: 1.5rem;
  }

  p {
    color: var(--text-gray-color);
    margin: 0;
    line-height: 1.5;
  }
`;

const PopupContent = styled.div`
  position: relative;
  background: white;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 100%;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
  }

  svg {
    color: #333;
    font-size: 1.2rem;
  }
`;

const PopupContainer = styled.div`
  display: flex;
  min-height: 500px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    max-height: 500px;
  }

  @media (max-width: 768px) {
    min-height: 250px;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .main-heading {
    color: #333;
    font-size: 2.2rem;
    font-weight: 300;
    margin: 0 0 20px 0;
    letter-spacing: -0.5px;
    line-height: 1.2;
  }

  .description {
    color: #666;
    font-size: 1.1rem;
    line-height: 1.6;
    margin: 0 0 30px 0;
  }

  .price-section {
    margin-bottom: 30px;

    .price-display {
      display: flex;
      align-items: center;
      gap: 10px;
      background: linear-gradient(135deg, var(--primary-light-color) 0%, rgba(var(--primary-color-rgb), 0.1) 100%);
      padding: 20px;
      border-radius: 12px;
      border: 2px solid var(--primary-color);

      .price-icon {
        color: var(--primary-color);
        font-size: 1.5rem;
      }

      .price {
        color: var(--primary-color);
        font-size: 2rem;
        font-weight: 700;
        margin-right: 10px;
      }

      .price-label {
        color: var(--text-gray-color);
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }
  }

  .action-section {
    .buy-btn {
      background: #333;
      color: white;
      border: none;
      padding: 15px 40px;
      border-radius: 0;
      font-weight: 700;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      margin-bottom: 15px;
      letter-spacing: 1px;

      &:hover {
        background: #222;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
      }
    }

    .disclaimer {
      color: #999;
      font-size: 0.8rem;
      margin: 0 0 20px 0;
      font-style: italic;
      text-align: center;
    }

    .skip-btn {
      background: transparent;
      color: #999;
      border: none;
      padding: 10px;
      font-size: 0.9rem;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: underline;

      &:hover {
        color: #666;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 30px 20px;

    .main-heading {
      font-size: 1.8rem;
    }
  }
`;
